import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { listAgentJobs, type JobWithLatestRun } from '@/lib/api/endpoints/agent-jobs';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import { queryKeys } from '@/lib/query/keys';
import { notify } from './notify';
import { detectRunCompletions } from './agentRunToast';

/**
 * Watches agent jobs and fires an in-app notification toast when a run finishes
 * (done/failed) — the real driver behind the notification pop-up. Renders
 * nothing; mounted once at the app shell. Shares the agent-jobs query cache with
 * the Agents module (same key), and self-polls (8s) only while a run is active.
 */
export function AgentRunNotifier() {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  // null = not yet baselined; a Map once we've recorded the first snapshot.
  const seenRef = useRef<Map<string, string> | null>(null);

  const q = useQuery({
    queryKey: queryKeys.agentJobs.list(workspaceId),
    queryFn: () => listAgentJobs(token!),
    enabled: Boolean(token && workspaceId),
    refetchInterval: (query) => {
      const items = (query.state.data as { items: JobWithLatestRun[] } | undefined)?.items ?? [];
      const active = items.some(
        (i) => i.latestRun && (i.latestRun.status === 'queued' || i.latestRun.status === 'running'),
      );
      return active ? 8000 : false;
    },
  });

  const data = q.data;
  // Re-baseline on workspace switch: the seen-run map is per-workspace. Without
  // this, switching workspaces leaves the old map in place, so the new
  // workspace's already-finished runs look "unseen" and pop false toasts.
  useEffect(() => {
    seenRef.current = null;
  }, [workspaceId]);

  useEffect(() => {
    if (!data) return;
    const items = data.items ?? [];
    const { fires, next } = detectRunCompletions(items, seenRef.current, Date.now());
    seenRef.current = next;
    fires.forEach(notify);
  }, [data]);

  return null;
}
